import { Injectable } from '@angular/core';
import { of } from 'rxjs';
import { HttpService } from '@core/services/http.service';
import moment from 'moment';
import { ChatConfig } from './chat-url.config';

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  public activeGroupIds: (number | string)[] = [];
  public activeGroupsLoaded: boolean = false;

  constructor(private http: HttpService) { }

  updateActiveGroups(groups: any[]) {
    const activeIds = new Set<string>();
    groups
      .filter(g => g.activeStatus !== false && g.isInactive !== true && !(g.inactiveReason || g.inactive_reason || '').includes('removed'))
      .forEach(g => {
        if (g.groupId) activeIds.add(String(g.groupId));
        if (g.id) activeIds.add(String(g.id));
        if (g.metaid) activeIds.add(String(g.metaid));
      });
    this.activeGroupIds = Array.from(activeIds);
    this.activeGroupsLoaded = true;
  }

  loadActiveGroupsGlobally() {
    if (this.activeGroupsLoaded) return;
    this.getGroupChats().subscribe((res: any) => {
      if (res && res.data) {
        this.updateActiveGroups(res.data);
      }
    });
  }

  getUserMessage(messagePayload) {
    return this.http.post(ChatConfig.EndPoint.chat.getUserMessage, messagePayload);
  }

  getUserConversation(messagePayload) {
    return this.http.post(ChatConfig.EndPoint.chat.getUserConversation, messagePayload);
  }
  getChatUsers(userId: number, case_id: number) {
    return this.http.get(ChatConfig.EndPoint.chat.getChatUsers.replace('{id}', String(userId)) + `${case_id ? '?caseid=' + case_id : ''}`);
  }

  getCaseList() {
    return this.http.get(ChatConfig.EndPoint.chat.getCaseList);
  }

  getUnreadMessages() {
    return this.http.get(ChatConfig.EndPoint.chat.getUnreadMessages);
  }

  getUserSession(id: any) {
    if (id === null || id === undefined || id === '' || isNaN(Number(id))) {
      return of({ data: null });
    }
    return this.http.get(ChatConfig.EndPoint.chat.getUserSession.replace('{id}', String(id)));
  }

  getOnlineUsers() {
    return this.http.get(ChatConfig.EndPoint.chat.getOnlineUsers);
  }

  getChatUsersByCase(id: number) {
    return this.http.get(ChatConfig.EndPoint.chat.getChatUsersByCase + `${id ? '?caseid=' + id : ''}`);
  }

  getAllChatUsers(case_id: number, personId:any) {
    return this.http.get(ChatConfig.EndPoint.chat.getAllChatUsers + `${case_id ? '?caseid=' + case_id : ''}` + `${personId ? '&personid=' + personId : ''}`);
  }

  addChatUsers(payload: any) {
    return this.http.post(ChatConfig.EndPoint.chat.addChatUsers, payload);
  }

  createGroupChat(payload: any) {
    return this.http.post(ChatConfig.EndPoint.chat.createGroupChat, payload);
  }

  getGroupChats(caseId?: number, childId?: number) {
    let url = ChatConfig.EndPoint.chat.getGroupChats + '?';
    if(caseId) url += 'caseId=' + caseId + '&';
    if(childId) url += 'childId=' + childId;
    return this.http.get(url);
  }

  getGroupParticipants(groupId: any) {
    return this.http.get(ChatConfig.EndPoint.chat.getParticipants.replace('{id}', String(groupId)));
  }

  deactivateGroupChat(groupId: string) {
    return this.http.patch(ChatConfig.EndPoint.chat.deactivateGroupChat.replace('{id}', groupId), {});
  }

  sendMessage(payload: any) {
    return this.http.post(ChatConfig.EndPoint.chat.sendMessage, payload);
  }

  getChildByCase(case_id: number) {
    return this.http.get(ChatConfig.EndPoint.chat.getChildByCase.replace('{case_id}', String(case_id)));
  }

  getSignedDocumentUrl(document) {
    return this.http.post(ChatConfig.EndPoint.document.getSignedDocumentUrl, document);
  }

  getDocumentCategories() {
    return this.http.get(ChatConfig.EndPoint.document.getDocumentCategories);
  }

  getDocumentTypes(parentKey: string) {
    return this.http.get(ChatConfig.EndPoint.document.getDocumentTypes.replace('{key}', parentKey));
  }

  addAttachment(payload: any) {
    return this.http.post(ChatConfig.EndPoint.document.addAttachment, payload)
  }

  updateDocumentStatus(payload: any) {
    return this.http.patch(ChatConfig.EndPoint.document.updateDocumentStatus.replace('{document_id}', payload.id), payload);
  }

  getAttachmentById(document_id: string) {
    return this.http.get(ChatConfig.EndPoint.document.getAttachmentById.replace('{document_id}', document_id));
  }

  formatBytes(byte) {
    const units = ['bytes', 'KB', 'MB', 'GB'];
    let index = 0;
    let bytes = parseInt(byte, 10) || 0;

    while (bytes >= 1024 && ++index) {
      bytes = bytes / 1024;
    }

    return bytes.toFixed(bytes < 10 && index > 0 ? 1 : 0) + ' ' + units[index];
  }

  getAbbrevation(name?: string) {
    const normalized = (name || '').toString().trim();
    if (!normalized) {
      return '';
    }
    return normalized
      .split(' ')
      .filter(part => part)
      .map(part => part.charAt(0).toLocaleUpperCase())
      .join('');
  }

  getFileExtension(fileType) {
  const fileExtension = [
    { type: 'text/plain', extension: 'TXT', icon: 'fa fa-file-text-o' },
    { type: 'application/pdf', extension: 'PDF', icon: 'fa fa-file-pdf-o' },
    { type: 'text/csv', extension: 'CSV', icon: 'fa fa-file-excel-o' },
    { type: 'application/json', extension: 'JSON', icon: 'fa fa-file-text-o' },
    { type: 'image/png', extension: 'PNG', icon: 'fa fa-file-picture-o' },
    { type: 'image/jpeg', extension: 'JPG', icon: 'fa fa-file-picture-o' },
    { type: 'image/jpg', extension: 'JPG', icon: 'fa fa-file-picture-o' },
    { type: 'image/tiff', extension: 'TIFF', icon: 'fa fa-file-picture-o' },
    { type: 'image/tif', extension: 'TIF', icon: 'fa fa-file-picture-o' },
    { type: 'application/msword', extension: 'DOC', icon: 'fa fa-file-word-o' },
    { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', extension: 'DOCX', icon: 'fa fa-file-word-o' }
  ];

  return fileExtension.find(file => file.type === fileType);
}


  getMessageDate(messageIndex: number, messages: Array<any>): string {
    const dateToday = moment().format('DD MMMM YYYY');
    const dateYesterday = moment().subtract(1, 'day').format('DD MMMM YYYY');
    const messageDate = moment(messages[messageIndex].createdat).format('DD MMMM YYYY');
    if (messageDate === dateToday) {
      return 'Today';
    } else if (messageDate === dateYesterday) {
      return 'Yesterday';
    } else {
      return messageDate;
    }
  }
}
